<template>
  <div
    class="selection-tab"
    :class="{
      'selection-tab--button': url,
      'selection-tab--large': showSizeLabel && sizeLabel,
      [$class('disabled')]: disabled,
      [$class('loading')]: loading,
    }"
  >
    <input
      v-if="!url"
      :id="id"
      v-model="localModelValue"
      class="selection-tab__input visually-hidden"
      :class="{ [$class('disabled')]: disabled }"
      :name="name"
      type="radio"
      :value="value"
      @input="updateVModel(value)"
    >

    <component
      :is="url ? 'a' : 'label'"
      class="
        selection-tab__label
        text-body-s-regular
        text-body-m-regular-desktop
      "
      :for="id"
      :href="url ? url : null"
      :title="title"
    >
      <span v-html="label" />
      <span
        v-if="showSizeLabel && sizeLabel"
        class="selection-tab__label--size"
        v-html="sizeLabel"
      />
    </component>
  </div>
</template>

<script>

/**
 * Vue: Selection tab (selection-tab)
 * -----------------------------------------------------------------------------
 * Simple tab button with active, disabled, and initial states.
 *
 * @param {Boolean} [disabled] - Tab is disabled.
 * @param {String} [id] - ID for input, required for input.
 * @param {String} label - Tab label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number|String} modelValue - Current value provided by v-model.
 * @param {String} [name] - Radio input group name, required for input.
 * @param {String} [title] - Title attribute.
 * @param {String} [url] - URL to navigate to when clicked, replaces input.
 * @param {Number|String} [value] - Radio input value, required for input.
 * @param {Boolean} [isMetric] - Indicates if the size label is in metric units.
 * @param {Boolean} [showSizeLabel] - Indicates if the size label should be shown.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
export default {
  name: 'SelectionTab',

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Required for v-model
    // eslint-disable-next-line vue/no-unused-properties
    modelValue: {
      type: [Number, String],
      default: '',
    },
    modelInfo: {
      type: [Boolean, String],
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String],
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    isMetric: {
      type: Boolean,
      default: true,
    },
    showSizeLabel: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: '',
    }
  },

  computed: {

    /**
     * Returns matching size height.
     */
    sizeLabel() {
      if (!this.showSizeLabel) {
        return null;
      }

      const sizeMap = {
        xs: this.isMetric ? this.$string('product.form.size_xs_height_cm') : this.$string('product.form.size_xs_height'),
        s: this.isMetric ? this.$string('product.form.size_s_height_cm') : this.$string('product.form.size_s_height'),
        m: this.isMetric ? this.$string('product.form.size_m_height_cm') : this.$string('product.form.size_m_height'),
        l: this.isMetric ? this.$string('product.form.size_l_height_cm') : this.$string('product.form.size_l_height'),
        xl: this.isMetric ? this.$string('product.form.size_xl_height_cm') : this.$string('product.form.size_xl_height'),
        xxl: this.isMetric ? this.$string('product.form.size_xxl_height_cm') : this.$string('product.form.size_xxl_height'),
      }

      // Extract just the size letter before any HTML content
      const sizeLetter = this.label.split(' <span')[0].trim().toLowerCase()
      return sizeMap[sizeLetter] || null
    }
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Number|String} value - Update value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './selection-tab';
</style>
